import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Paperclip, Bot, Building2, Plane, Sparkles, Loader2, Zap, Globe, Car, Home, CreditCard, FileText, Bell, File as FileIcon } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useAIChat } from '../config/aiChatContext';
import VoiceRecorder from './VoiceRecorder';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Route → bot section key (shared vocabulary with the backend and the mobile app)
const SECTION_RULES = [
  [/company-formation/, 'company_formation'],
  [/\/services\/visa/, 'visa'],
  [/translation/, 'translation_services'],
  [/\/services\/documents/, 'document_clearing'],
  [/^\/(services|service-packages)/, 'services'],
  [/numbers|^\/phone\//, 'numbers'],
  [/plates|^\/plate\//, 'plates'],
  [/^\/(cars|car\/)|marketplace\/cars|market\/cars/, 'cars'],
  [/real-estate|^\/propert/, 'real_estate'],
  [/^\/(products|product\/|store\/|spare-parts|cart|checkout)/, 'products'],
  [/^\/buy-requests/, 'buy_requests'],
  [/^\/(dashboard|verify-identity)/, 'account'],
];
const sectionForPath = (path) => (SECTION_RULES.find(([re]) => re.test(path)) || [null, 'homepage'])[1];


// Logos for different themes
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/gl2mwiwe_CLOUDGM%20MARKET%20WHITE.png"; // White text for dark mode
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/vu2e3mcb_CLOUDGM%20MARKET%20BLACK.png"; // Black text for light mode

// Agent types with their configurations
const AGENTS = {
  general: {
    id: 'general',
    name: { ar: 'CGM AI AGENT', en: 'CGM AI AGENT' },
    icon: Sparkles,
    color: '#1E9BD9',
    description: { ar: 'مساعدك الذكي', en: 'Your Smart Assistant' }
  },
  company: {
    id: 'company',
    name: { ar: 'تأسيس الشركات', en: 'Company Formation' },
    icon: Building2,
    color: '#10B981',
    description: { ar: 'خبير تأسيس الشركات في الإمارات', en: 'UAE Company Formation Expert' }
  },
  visa: {
    id: 'visa',
    name: { ar: 'التأشيرات', en: 'Visa Services' },
    icon: Plane,
    color: '#F59E0B',
    description: { ar: 'خبير التأشيرات والإقامة', en: 'Visa & Residency Expert' }
  }
};

// AI Capabilities for welcome message
const AI_CAPABILITIES = {
  ar: [
    { icon: Globe, text: 'تصفح الموقع بالكامل' },
    { icon: Car, text: 'اختيار السيارة المثالية' },
    { icon: Building2, text: 'تأسيس شركتك في الإمارات' },
    { icon: Plane, text: 'التأشيرات والإقامة' },
    { icon: CreditCard, text: 'الأرقام واللوحات المميزة' },
    { icon: FileText, text: 'الترجمة القانونية' },
  ],
  en: [
    { icon: Globe, text: 'Browse entire site' },
    { icon: Car, text: 'Choose the perfect car' },
    { icon: Building2, text: 'Start your UAE company' },
    { icon: Plane, text: 'Visas & Residency' },
    { icon: CreditCard, text: 'Premium numbers & plates' },
    { icon: FileText, text: 'Legal translation' },
  ]
};

export const AIChatWidget = () => {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const { isOpen, setIsOpen, notification, dismissNotification, showNotification, getPageSuggestion, pendingPrompt, consumePendingPrompt } = useAIChat();
  const location = useLocation();
  const isAr = language === 'ar';
  
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentAgent, setCurrentAgent] = useState('general');
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Show page-specific notification on route change
  useEffect(() => {
    if (!isOpen) {
      const suggestion = getPageSuggestion(location.pathname, language);
      if (suggestion) {
        // Small delay to let page load
        const timer = setTimeout(() => {
          showNotification(suggestion, location.pathname);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname, isOpen, language, getPageSuggestion, showNotification]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // A prompt handed over from the hero AI bar → auto-send once the panel is open
  useEffect(() => {
    if (isOpen && pendingPrompt && !isLoading) {
      const p = consumePendingPrompt();
      if (p) setTimeout(() => sendMessage(p), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pendingPrompt]);

  // File upload handler (images / PDFs)
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingFile(true);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(isAr ? `${file.name} أكبر من 5MB` : `${file.name} exceeds 5MB`);
        continue;
      }
      const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowed.includes(file.type)) {
        toast.error(isAr ? 'نوع الملف غير مدعوم (صور أو PDF فقط)' : 'Unsupported file (images or PDF only)');
        continue;
      }
      try {
        const token = localStorage.getItem('token');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('session_id', sessionId);
        const resp = await axios.post(`${BACKEND_URL}/api/upload/chat-attachment`, fd, {
          headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'multipart/form-data' },
        });
        if (resp.data?.success) {
          setAttachments(prev => [...prev, {
            id: resp.data.file_id, name: file.name, type: file.type, url: resp.data.url, size: file.size,
          }]);
        }
      } catch (err) {
        console.error('Upload error:', err);
        toast.error(isAr ? `فشل رفع ${file.name}` : `Failed to upload ${file.name}`);
      }
    }
    setUploadingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  // Send message to AI
  const sendMessage = async (overrideText = null) => {
    const typed = (overrideText != null ? overrideText : inputValue).trim();
    if ((!typed && attachments.length === 0) || isLoading) return;

    const userMessage = typed;
    const currentAttachments = [...attachments];
    setInputValue('');
    setAttachments([]);
    
    // Add user message
    const newUserMessage = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      attachments: currentAttachments,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);

    setIsLoading(true);

    // Map current route to the bot's section (same keys the mobile app sends)
    const path = location.pathname || '';
    const chatContext = sectionForPath(path);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/ai/chat`, {
        message: userMessage,
        context: chatContext,
        agent: currentAgent,
        session_id: sessionId,
        language: language,
        history: messages.filter(m => m.role !== 'system').slice(-10),
        attachments: currentAttachments.map(a => ({ url: a.url, name: a.name, type: a.type })),
      });

      if (response.data.response) {
        const aiMessage = {
          id: Date.now() + 2,
          role: 'assistant',
          content: response.data.response,
          agent: currentAgent,
          handoff: response.data.handoff || false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMessage]);
      }
    } catch (error) {
      console.error('AI Chat Error:', error);
      const errorMessage = {
        id: Date.now() + 2,
        role: 'assistant',
        content: isAr 
          ? 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.' 
          : 'Sorry, an error occurred. Please try again.',
        agent: currentAgent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle voice transcription — AUTO-SEND the spoken message as if the user typed and pressed enter.
  const handleTranscript = (text) => {
    if (!text || !text.trim()) return;
    setInputValue(text);
    // Give React a tick to flush state, then dispatch send.
    setTimeout(() => sendMessage(text), 0);
  };

  // One-shot voice turn: audio → (transcript + reply) in a single round-trip.
  const handleVoiceAudio = async (blob, mime) => {
    if (isLoading) return;
    const path = location.pathname || '';
    const chatContext = sectionForPath(path);

    const pendingId = Date.now();
    setMessages(prev => [...prev, {
      id: pendingId, role: 'user', content: isAr ? '🎤 رسالة صوتية…' : '🎤 Voice message…', voice: true, timestamp: new Date(),
    }]);
    setIsLoading(true);
    try {
      const fd = new FormData();
      const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
      fd.append('file', blob, `voice.${ext}`);
      fd.append('language', isAr ? 'ar' : 'en');
      fd.append('context', chatContext);
      fd.append('session_id', sessionId);
      fd.append('history', JSON.stringify(
        messages.filter(m => m.role !== 'system').slice(-8).map(m => ({ role: m.role, content: m.content }))
      ));
      const response = await axios.post(`${BACKEND_URL}/api/ai/chat/voice`, fd, { timeout: 90000 });
      const transcript = response.data?.transcript;
      setMessages(prev => prev.map(m => (m.id === pendingId && transcript ? { ...m, content: transcript } : m)));
      if (response.data?.response) {
        setMessages(prev => [...prev, {
          id: Date.now() + 2, role: 'assistant', content: response.data.response,
          agent: currentAgent, handoff: response.data.handoff || false, timestamp: new Date(),
        }]);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message;
      setMessages(prev => prev.filter(m => m.id !== pendingId));
      toast.error(msg || (isAr ? 'لم أتمكن من فهم الصوت، حاول مجدداً' : "Couldn't understand the audio, try again"));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const agent = AGENTS[currentAgent];
  const AgentIcon = agent.icon;
  const capabilities = AI_CAPABILITIES[isAr ? 'ar' : 'en'];

  // Handle notification click to open chat
  const handleNotificationClick = () => {
    dismissNotification(location.pathname);
    setIsOpen(true);
  };

  return (
    <>
      {/* Smart Notification Popup */}
      {notification && !isOpen && (
        <div 
          className={`fixed bottom-24 right-6 z-50 max-w-[320px] animate-slideUp cursor-pointer`}
          onClick={handleNotificationClick}
        >
          <div className={`relative rounded-2xl p-4 shadow-2xl ${isDarkMode ? 'bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] border border-white/10' : 'bg-white border border-gray-200'}`}>
            {/* Close button */}
            <button 
              onClick={(e) => { e.stopPropagation(); dismissNotification(location.pathname); }}
              className={`absolute top-2 end-2 p-1 rounded-full transition-colors ${isDarkMode ? 'hover:bg-white/10 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="relative flex-shrink-0">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/5'}`}>
                  <img src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} alt="AI" className="w-8 h-8 object-contain" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
              </div>
              <div className="flex-1 pt-1">
                <p className={`text-sm font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                  CGM AI AGENT
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-slate-700'}`}>
                  {notification}
                </p>
                <button className="mt-2 text-xs font-semibold text-[#0284C7] hover:underline">
                  {isAr ? 'اضغط للدردشة' : 'Click to chat'}
                </button>
              </div>
            </div>
            
            {/* Arrow pointing to chat button */}
            <div className={`absolute -bottom-2 right-8 w-4 h-4 rotate-45 ${isDarkMode ? 'bg-[#0d0d0d] border-r border-b border-white/10' : 'bg-white border-r border-b border-gray-200'}`} />
          </div>
        </div>
      )}

      {/* Floating Button with Company Logo */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-4 sm:right-6 z-[9999] group"
          data-testid="ai-chat-button"
        >
          {/* Pulse ring effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#1E9BD9] to-[#F59E0B] opacity-30 blur-md animate-pulse scale-125" />
          
          {/* Main button with logo - bigger on mobile */}
          <div className={`relative w-16 h-16 sm:w-14 sm:h-14 rounded-full shadow-2xl transition-all duration-300 group-hover:scale-110 group-hover:shadow-[#1E9BD9]/40 flex items-center justify-center ${isDarkMode ? 'bg-[#1a1a1a] border-2 border-[#1E9BD9]/50' : 'bg-white border-2 border-[#1E9BD9]/30'}`}>
            <img 
              src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} 
              alt="CloudGM AI" 
              className="w-10 h-10 sm:w-9 sm:h-9 object-contain"
            />
          </div>
          
          {/* Online indicator dot - positioned outside */}
          <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-white shadow-md animate-pulse" />
          
          {/* Notification badge if there's a notification */}
          {notification && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center animate-bounce">
              <Bell className="w-3 h-3 text-white" />
            </div>
          )}
          
          {/* Tooltip */}
          <div className={`absolute bottom-full mb-3 right-0 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 ${isDarkMode ? 'bg-white text-[#0a0a0a]' : 'bg-[#0a0a0a] text-white'}`}>
            {isAr ? 'تحدث مع CGM AI' : 'Chat with CGM AI'}
            <div className={`absolute top-full right-6 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent ${isDarkMode ? 'border-t-white' : 'border-t-[#0a0a0a]'}`} />
          </div>
        </button>
      )}

      {/* Chat Window - BIGGER */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 z-50 w-[420px] max-w-[calc(100vw-48px)] rounded-2xl overflow-hidden shadow-2xl animate-scaleIn flex flex-col ${isDarkMode ? 'bg-[#0a0a0a] border border-white/10' : 'bg-white border border-gray-200'}`}
          style={{ height: '620px', maxHeight: 'calc(100vh - 100px)' }}
          data-testid="ai-chat-window"
        >
          {/* Header */}
          <div className="relative px-4 py-3 bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] flex-shrink-0">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '16px 16px' }} />
            </div>
            
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/20"
                >
                  <img src={LOGO_DARK} alt="CGM AI" className="w-6 h-6 object-contain" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm">
                    CGM AI
                  </h3>
                  <p className="text-xs text-white/70">
                    {isAr ? 'مساعدك الذكي' : 'Your Smart Assistant'}
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-xl hover:bg-white/10 transition-colors"
                data-testid="close-chat-btn"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div 
            className={`flex-1 overflow-y-auto p-4 space-y-3 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-gray-50'}`}
            dir={isAr ? 'rtl' : 'ltr'}
          >
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full">
                {/* Welcome Header - Simple */}
                <div className="text-center">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${isDarkMode ? 'bg-gradient-to-br from-[#1E9BD9]/20 to-[#F59E0B]/20' : 'bg-gradient-to-br from-[#1E9BD9]/10 to-[#F59E0B]/10'}`}>
                    <img src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} alt="AI" className="w-9 h-9 object-contain" />
                  </div>
                  <h4 className={`font-bold text-base mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                    {isAr ? 'أهلاً! 👋' : 'Hello! 👋'}
                  </h4>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isAr 
                      ? 'كيف أقدر أساعدك اليوم؟' 
                      : 'How can I help you today?'}
                  </p>
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div key={message.id}>
                {message.role === 'system' ? (
                  <div className="flex justify-center">
                    <span className={`text-xs px-3 py-1 rounded-full ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                      {message.content}
                    </span>
                  </div>
                ) : (
                  <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] ${message.role === 'user' ? 'order-2' : 'order-1'}`}>
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center overflow-hidden ${isDarkMode ? 'bg-[#1a1a1a] border border-white/20' : 'bg-white border border-gray-200'}`}>
                            <img src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} alt="AI" className="w-4 h-4 object-contain" />
                          </div>
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {AGENTS[message.agent || currentAgent].name[isAr ? 'ar' : 'en']}
                          </span>
                        </div>
                      )}
                      <div 
                        className={`px-3.5 py-2.5 rounded-xl ${
                          message.role === 'user'
                            ? 'bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] rounded-br-sm'
                            : isDarkMode 
                              ? 'bg-[#1a1a1a] text-white border border-white/10 rounded-bl-sm' 
                              : 'bg-white text-[#0f172a] border border-gray-200 rounded-bl-sm shadow-sm'
                        }`}
                      >
                        <p className={`whitespace-pre-wrap leading-relaxed text-sm ${message.role === 'user' ? 'text-white font-medium' : ''}`}>{message.content}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className={`px-3 py-2 rounded-xl rounded-bl-sm ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border border-gray-200'}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#1E9BD9] animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-[#1E9BD9] animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-[#1E9BD9] animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {isAr ? 'جاري التفكير...' : 'Thinking...'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={`p-3 border-t flex-shrink-0 ${isDarkMode ? 'bg-[#0a0a0a] border-white/5' : 'bg-white border-gray-200'}`}>
            {/* Attachments preview */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="widget-attachments-preview">
                {attachments.map(att => (
                  <div key={att.id} className="relative group">
                    {att.type?.startsWith('image/') ? (
                      <img src={att.url} alt={att.name} className="h-12 w-12 object-cover rounded-lg border border-gray-200" />
                    ) : (
                      <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg border border-gray-200 text-xs">
                        <FileIcon className="h-3 w-3 text-[#1E9BD9]" />
                        <span className="truncate max-w-[80px]">{att.name}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(att.id)}
                      className="absolute -top-1 -end-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-gray-100 border border-gray-200'}`}>
              {/* Attachment button */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="widget-file-input"
              />
              {!isVoiceRecording && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile || isLoading}
                  className={`p-1.5 rounded-lg transition-all ${isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-500'} disabled:opacity-40`}
                  title={isAr ? 'إرفاق ملف' : 'Attach file'}
                  data-testid="widget-attach-btn"
                >
                  {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin text-[#1E9BD9]" /> : <Paperclip className="w-4 h-4" />}
                </button>
              )}

              {/* Voice recorder — expands to full-width recording bar when active */}
              <VoiceRecorder
                onTranscript={handleTranscript}
                onAudio={handleVoiceAudio}
                onRecordingChange={setIsVoiceRecording}
                language={isAr ? 'ar' : 'en'}
                variant={isDarkMode ? 'pill' : 'light'}
                testId="widget-voice-btn"
                disabled={isLoading}
              />

              {!isVoiceRecording && (
                <>
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isAr ? 'اكتب رسالتك...' : 'Type your message...'}
                    className={`flex-1 bg-transparent outline-none text-sm min-w-0 ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-[#0f172a] placeholder-gray-400'}`}
                    disabled={isLoading}
                    data-testid="chat-input"
                    dir={isAr ? 'rtl' : 'ltr'}
                  />

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={sendMessage}
                      disabled={(!inputValue.trim() && attachments.length === 0) || isLoading}
                      className={`p-1.5 rounded-lg transition-all ${
                        (inputValue.trim() || attachments.length > 0) && !isLoading
                          ? 'bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white hover:shadow-lg hover:shadow-[#1E9BD9]/30'
                          : isDarkMode 
                            ? 'bg-white/5 text-gray-600' 
                            : 'bg-gray-200 text-gray-400'
                      }`}
                      data-testid="send-message-btn"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </>
              )}
            </div>
            <p className={`text-center text-[10px] mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              CLOUDGM AI
            </p>
          </div>
        </div>
      )}

      {/* CSS for gradient animation */}
      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </>
  );
};

export default AIChatWidget;
